import type { ListenerEffectAPI, PayloadAction } from "@reduxjs/toolkit";
import { createListenerMiddleware } from "@reduxjs/toolkit";

import { setActiveAccountEid } from "@/redux/app/appReducers";
import { selectAccountList, selectActiveAccountEid, selectAclMenusByActiveEid } from "@/redux/app/appSelectors";
import type { AppDispatch, RootState } from "@/redux/store";
import { endpoints as yieldEndpoints } from "@/routes/_authenticated/yield/-api/yield";
import { endpoints as accountEndpoints } from "@/shared/api/account";
import { endpoints as opportunityEndpoints } from "@/shared/api/opportunities";
import { endpoints as policyEndpoints } from "@/shared/api/policy";
import type { TApiPolicyPayload } from "@/shared/api/types/clientPortal";
import { ACCEPTED_COOKIES_KEY } from "@/util/compliantStorage";

export const handlePolicyLoaded = (
  action: PayloadAction<TApiPolicyPayload>,
  { dispatch, getState }: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const accountList = selectAccountList(getState());

  if (accountList.length > 0) {
    dispatch(accountEndpoints.getAccountNames.initiate(accountList, { subscribe: false }));
  }

  // selectActiveAccountEid synthesizes accountList[0] when storage is empty, so persist
  // must look at the stored value — otherwise first-account never lands in localStorage.
  const storedAccount = getState().app.activeAccount;
  const resolvedAccountEid = selectActiveAccountEid(getState()) ?? accountList[0];

  if (resolvedAccountEid && storedAccount !== resolvedAccountEid) {
    dispatch(setActiveAccountEid(resolvedAccountEid));
  }

  const { cookieConsent } = action.payload.settings;
  if (typeof cookieConsent?.consentGiven !== "undefined") {
    localStorage.setItem(ACCEPTED_COOKIES_KEY, String(cookieConsent.consentGiven ?? false));
  }

  if (!resolvedAccountEid) return;

  // Re-read after persist so ACL menus see the stored active account, matching the saga's
  // yield select() after put(setActiveAccountEid).
  const aclMenus = selectAclMenusByActiveEid(getState());

  if (aclMenus.includes("balance")) dispatch(accountEndpoints.getAccountPosition.initiate(resolvedAccountEid));
  if (aclMenus.includes("investment")) dispatch(yieldEndpoints.getInterestAndTax.initiate(resolvedAccountEid));
  if (aclMenus.includes("opportunities")) dispatch(opportunityEndpoints.getOpportunities.initiate(resolvedAccountEid));
};

export const appListenerMiddleware = createListenerMiddleware();

appListenerMiddleware.startListening({
  matcher: policyEndpoints.getPolicyForCognitoUser.matchFulfilled,
  effect: (action, api) => handlePolicyLoaded(action, api as ListenerEffectAPI<RootState, AppDispatch>),
});
